import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Plus, Pencil } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNotifications } from "@/hooks/useNotifications";
import { useQueryClient } from "@tanstack/react-query";

interface PostFormProps {
  editingId: string | null;
  gymId?: string;
  onSuccess: () => void;
  initialData?: {
    title: string;
    content: string;
  };
}

export const PostForm = ({ editingId, gymId, onSuccess, initialData }: PostFormProps) => {
  const [title, setTitle] = useState(initialData?.title || "");
  const [content, setContent] = useState(initialData?.content || "");
  const [image, setImage] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { success, error: showError } = useNotifications();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title);
      setContent(initialData.content);
    }
  }, [initialData]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImage(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validación: gymId es requerido para crear/editar posts
    if (!gymId) {
      showError("No se puede crear un post sin un gimnasio asignado");
      return;
    }
    
    try {
      let imageUrl = null;

      if (image) {
        const fileExt = image.name.split(".").pop();
        const filePath = `${crypto.randomUUID()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from("images")
          .upload(filePath, image);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from("images")
          .getPublicUrl(filePath);

        imageUrl = publicUrl;
      }

      if (editingId) {
        const { error } = await supabase
          .from("posts")
          .update({
            title,
            content,
            ...(imageUrl && { image_url: imageUrl }),
            gym_id: gymId // Asegurar que el gym_id se mantenga en actualizaciones
          })
          .eq("id", editingId);

        if (error) {
          console.error("Error updating post:", error);
          throw error;
        }
        success("Post actualizado correctamente");
      } else {
        const { error } = await supabase
          .from("posts")
          .insert([{ 
            title, 
            content, 
            image_url: imageUrl,
            gym_id: gymId 
          }]);

        if (error) {
          console.error("Error creating post:", error);
          throw error;
        }
        success("Post creado correctamente");
      }

      // Invalidate all post-related queries to ensure immediate updates across the app
      await queryClient.invalidateQueries({ queryKey: ["posts"] });

      setTitle("");
      setContent("");
      setImage(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      onSuccess();
    } catch (error: any) {
      console.error("PostForm submission error:", error);
      showError(error.message?.includes("policy") 
        ? "No tienes permisos para realizar esta acción en este gimnasio"
        : "No se pudo guardar el post");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        placeholder="Título del post"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        required
      />
      <Textarea
        placeholder="Contenido del post"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        required
        className="min-h-[100px]"
      />
      <div className="flex items-center gap-4">
        <Input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageChange}
          className="flex-1 h-11 cursor-pointer file:text-white file:bg-primary file:rounded-md file:border-0 file:px-4 file:py-1.5 file:mr-3 file:cursor-pointer file:text-sm file:font-medium file:hover:bg-primary/80 file:transition-colors file:-mt-0.5"
        />
        <Button type="submit">
          {editingId ? (
            <>
              <Pencil className="mr-2 h-4 w-4" />
              Actualizar
            </>
          ) : (
            <>
              <Plus className="mr-2 h-4 w-4" />
              Crear
            </>
          )}
        </Button>
      </div>
    </form>
  );
};